import { useSyncExternalStore } from 'react';

export const UNAUTHORIZED_STATUS = 401;
export const TENANT_OFF_STATUS = 403;

export interface LiveAssistCallStanding {
  liveSession: boolean;
  refused: boolean;
}

interface CallMemory {
  liveSessions: number[];
  refused: number[];
  unauthorized: string[];
  tenantOff: boolean;
}

const FRESH: CallMemory = { liveSessions: [], refused: [], unauthorized: [], tenantOff: false };

let memory: CallMemory = FRESH;
const listeners = new Set<() => void>();

const publish = (next: CallMemory): void => {
  memory = next;
  listeners.forEach(listener => {
    listener();
  });
};

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const snapshot = (): CallMemory => memory;

const isId = (value: number | undefined): value is number => typeof value === 'number';

const attemptKey = (callId: number, attempt: number): string => `${callId}:${attempt}`;

const standingIn = (
  state: CallMemory,
  callId: number | undefined,
  attempt: number,
): LiveAssistCallStanding => ({
  liveSession: isId(callId) && state.liveSessions.includes(callId),
  refused:
    state.tenantOff ||
    (isId(callId) &&
      (state.refused.includes(callId) || state.unauthorized.includes(attemptKey(callId, attempt)))),
});

export const rememberLiveAssistLiveSession = (callId: number): void => {
  if (memory.liveSessions.includes(callId)) return;
  publish({ ...memory, liveSessions: [...memory.liveSessions, callId] });
};

const rememberUnauthorized = (callId: number, attempt: number): void => {
  const key = attemptKey(callId, attempt);
  if (memory.unauthorized.includes(key)) return;
  publish({ ...memory, unauthorized: [...memory.unauthorized, key] });
};

export const rememberLiveAssistRefusal = (callId: number, status: number, attempt = 0): void => {
  if (status === UNAUTHORIZED_STATUS) {
    rememberUnauthorized(callId, attempt);
    return;
  }
  const tenantOff = memory.tenantOff || status === TENANT_OFF_STATUS;
  const known = memory.refused.includes(callId);
  if (known && tenantOff === memory.tenantOff) return;
  publish({ ...memory, tenantOff, refused: known ? memory.refused : [...memory.refused, callId] });
};

export const liveAssistCallStanding = (
  callId: number | undefined,
  attempt = 0,
): LiveAssistCallStanding => standingIn(memory, callId, attempt);

export const useLiveAssistCallStanding = (
  callId: number | undefined,
  attempt = 0,
): LiveAssistCallStanding => standingIn(useSyncExternalStore(subscribe, snapshot), callId, attempt);

export const forgetLiveAssistCalls = (): void => {
  publish(FRESH);
};
